import { Button, TableCol, TablePagination, TableSort, Table } from 'zens-storybook'
import { StyleSheet, ViewProps } from 'react-native'
import { useTranslation } from 'react-i18next'
import { AccessType, Building, useBuildings } from 'zens-store'
import { usePermissions } from '@dms/hook'

import { Link } from 'zens-router'

import { Panel } from '../layout'
import { TablePaginated } from '../table'

import BuildingEmpty from './empty'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
})

interface Data {
  data: Building
  onPress?: () => void
}

interface Props extends ViewProps {
  companyId: number
  locationId: number
  pagination?: TablePagination
  size?: number
  onPaginationChange?: (pagination: TablePagination) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
}

const BuildingTable = (props: Props) => {
  const {
    companyId,
    locationId,
    pagination = { page: 0, size: 10 },
    onPaginationChange,
    sort,
    onSortChange,
    style,
    ...otherProps
  } = props
  const { t } = useTranslation()
  const buildings = useBuildings({ companyId, locationId })
  const { hasPermission } = usePermissions(companyId)

  const cols: TableCol<Location>[] = [
    {
      key: 'name',
      title: t('Name'),
    },
    {
      key: 'numberOfFloors',
      title: t('Floors'),
    },
    {
      key: 'numberOfWorkspaces',
      title: t('Workspaces'),
    },
  ]
  const rows: Data[] =
    buildings.data?.map((building) => ({
      data: building,
      href: `/companies/${companyId}/locations/${locationId}/buildings/${building.id}`,
    })) || []

  return (
    <Panel
      style={[styles.container, style]}
      title={t('Buildings')}
      right={
        rows.length > 0 &&
        hasPermission(AccessType.Create, 'building') && (
          <Link href={`/companies/${companyId}/locations/${locationId}/buildings/add`} asChild>
            <Button title={t('Add building')} left="plus" testID="BuildingList.Add" />
          </Link>
        )
      }
      {...otherProps}
    >
      <TablePaginated
        rows={rows}
        cols={cols}
        pagination={pagination}
        count={rows.length}
        renderCount={() =>
          t('{{page}} of {{numberOfPages}}', {
            page: pagination.page + 1,
            numberOfPages: Math.ceil(rows.length / pagination.size),
          })
        }
        sizeLabel={t('Buildings per page')}
        maxSize={1000}
        onPaginationChange={onPaginationChange}
        sort={sort}
        onSortChange={onSortChange}
        ListEmptyComponent={<BuildingEmpty companyId={companyId} locationId={locationId} />}
      />
    </Panel>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const cols: TableCol<Location>[] = [
    {
      key: 'name',
      title: t('Name'),
    },
    {
      key: 'numberOfFloors',
      title: t('Floors'),
    },
    {
      key: 'numberOfWorkspaces',
      title: t('Workspaces'),
    },
  ]
  return (
    <Panel.Skeleton
      style={[styles.container, style]}
      title={t('Buildings')}
      right={<Button.Skeleton />}
      {...otherProps}
    >
      <Table.Skeleton cols={cols} hasAccessory />
    </Panel.Skeleton>
  )
}
BuildingTable.Skeleton = Skeleton

export default BuildingTable
